import { dateFromComponentsInTimezone } from '@/lib/scheduling/timezone';

/**
 * `Intl.DateTimeFormat` construction is the expensive part of a format call,
 * and `calendarDateIn` is now called once per event per render on the calendar
 * grids. One formatter per timezone, built lazily and reused forever — the
 * options are fixed, so a per-timezone instance is all the cache key needs.
 */
const calendarDayFormatters = new Map<string, Intl.DateTimeFormat>();

function calendarDayFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = calendarDayFormatters.get(timeZone);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  });
  calendarDayFormatters.set(timeZone, formatter);
  return formatter;
}

/**
 * Formats a Date as a `'YYYY-MM-DD'` calendar-day string in the given IANA timezone.
 */
export function calendarDateIn(date: Date, timeZone: string): string {
  return calendarDayFormatter(timeZone).format(date);
}

/**
 * Returns today's calendar-day string (`'YYYY-MM-DD'`) in the given IANA timezone.
 */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return calendarDateIn(now, timeZone);
}

/**
 * Returns whether two Dates fall on the same calendar day in the given IANA timezone.
 */
export function sameCalendarDay(a: Date, b: Date, timeZone: string): boolean {
  return calendarDateIn(a, timeZone) === calendarDateIn(b, timeZone);
}

/**
 * Shift a `'YYYY-MM-DD'` calendar-day string by whole days, purely as civil
 * arithmetic (no instant, no timezone, no DST). `Date.UTC` normalizes month
 * and year rollover, so `addCalendarDaysToYmd('2026-12-31', 1)` is
 * `'2027-01-01'`.
 */
export function addCalendarDaysToYmd(ymd: string, days: number): string {
  const [year, month, day] = ymd.split('-').map(Number);
  return calendarDateIn(new Date(Date.UTC(year, month - 1, day + days)), 'UTC');
}

/**
 * The instant at which a `'YYYY-MM-DD'` calendar day starts in `timeZone`.
 *
 * This is the one conversion from "a day people talk about" to "an instant a
 * comparison can use". It is DST-correct: on a spring-forward day the returned
 * instant is the real local midnight, not midnight-minus-an-hour.
 */
export function startOfCalendarDayIn(ymd: string, timeZone: string): Date {
  const [year, month, day] = ymd.split('-').map(Number);
  return dateFromComponentsInTimezone(year, month, day, 0, 0, 0, timeZone);
}
